"use client";

import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import * as z from "zod/v4";
import { mutationOptions, useMutation } from "@tanstack/react-query";

import { Alert, AlertDescription } from "@workspace/ui/components/alert";
import { Button } from "@workspace/ui/components/button";
import { Checkbox } from "@workspace/ui/components/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import { Input } from "@workspace/ui/components/input";
import { Label } from "@workspace/ui/components/label";
import {
	revalidateLogic,
	useAppForm,
} from "@workspace/ui/components/tanstack-form";

import { courseManagementApi } from "../api";
import type { CreateCourseInput } from "../types";

interface CreateCourseDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	tenant: string;
	venueId: string;
	nextSortOrder: number;
}

const createCourseSchema = z.object({
	name: z
		.string()
		.min(1, "Course name is required")
		.max(100, "Course name must not exceed 100 characters"),
	autoFire: z.boolean(),
});

type CreateCourseFormValues = z.infer<typeof createCourseSchema>;

export function CreateCourseDialog({
	open,
	onOpenChange,
	tenant,
	venueId,
	nextSortOrder,
}: CreateCourseDialogProps) {
	const mutation = useMutation(
		mutationOptions({
			...courseManagementApi.createCourseOptions(tenant),
			onSuccess: (result, _variables, _onMutateResult, context) => {
				if (result.success) {
					toast.success("Course created successfully.");
					context.client.invalidateQueries({
						queryKey: [tenant, "courses"],
					});
					handleClose(false);
				} else {
					const errorDetails = result.details as
						{ detail?: string } | undefined;
					toast.error(
						errorDetails?.detail ||
							"Failed to create course. Please try again.",
					);
				}
			},
			onError: () => {
				toast.error("Unable to create course. Please try again.");
			},
		}),
	);

	const form = useAppForm({
		defaultValues: {
			name: "",
			autoFire: false,
		} as CreateCourseFormValues,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: {
			onDynamic: createCourseSchema,
		},
		onSubmit: async ({ value }) => {
			const input: CreateCourseInput = {
				name: value.name.trim(),
				venueId,
				autoFire: value.autoFire,
				sortOrder: nextSortOrder,
			};
			await mutation.mutateAsync(input);
		},
	});

	const handleClose = (o: boolean) => {
		if (!o) {
			form.reset();
			mutation.reset();
		}
		onOpenChange(o);
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Create Course</DialogTitle>
					<DialogDescription>
						Add a new course to control the firing order for kitchen items.
					</DialogDescription>
				</DialogHeader>

				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
					className="space-y-4"
				>
					{mutation.isError && (
						<Alert variant="destructive">
							<AlertCircle className="h-4 w-4" />
							<AlertDescription>
								Unable to create course. Please try again.
							</AlertDescription>
						</Alert>
					)}

					<form.AppField name="name">
						{(field) => (
							<field.FormItem>
								<field.FormLabel>Course Name</field.FormLabel>
								<field.FormControl>
									<Input
										name={field.name}
										placeholder="e.g., Starters, Mains, Desserts"
										value={field.state.value}
										onChange={(e) => field.handleChange(e.target.value)}
										onBlur={field.handleBlur}
									/>
								</field.FormControl>
								<field.FormMessage />
							</field.FormItem>
						)}
					</form.AppField>

					<form.AppField name="autoFire">
						{(field) => (
							<field.FormItem>
								<div className="flex items-center space-x-2">
									<Checkbox
										id="autoFire"
										checked={field.state.value}
										onCheckedChange={(checked) =>
											field.handleChange(checked === true)
										}
									/>
									<Label htmlFor="autoFire" className="text-sm">
										Auto-fire
									</Label>
								</div>
								<field.FormDescription>
									When enabled, items in this course are sent automatically when
									the order is saved. The course is excluded from the firing
									sequence.
								</field.FormDescription>
							</field.FormItem>
						)}
					</form.AppField>

					<DialogFooter>
						<form.Subscribe
							selector={(state) => [state.canSubmit, state.isSubmitting]}
						>
							{([canSubmit]) => (
								<>
									<Button
										type="button"
										variant="outline"
										onClick={() => handleClose(false)}
										disabled={mutation.isPending}
									>
										Cancel
									</Button>
									<Button
										type="submit"
										disabled={!canSubmit || mutation.isPending}
									>
										{mutation.isPending && (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										)}
										Create Course
									</Button>
								</>
							)}
						</form.Subscribe>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
